const MAX_TENCENT_FILE_SIZE_BYTES = 6 * 1024 * 1024
const MAX_TENCENT_LOCAL_FILE_SIZE_BYTES = 806596 // 806596 bytes is the max file size for the local environment
const isLocalEnv = process.env.RAILS_ENV === 'development'
const maxFileSizeBytes = isLocalEnv ? MAX_TENCENT_LOCAL_FILE_SIZE_BYTES : MAX_TENCENT_FILE_SIZE_BYTES
const MIN_TENCENT_SIDE_PX = 128
const MAX_TENCENT_SIDE_PX = 5000

export function clampDimensions(width, height) {
  let sourceWidth = width
  let sourceHeight = height
  if (sourceWidth < MIN_TENCENT_SIDE_PX || sourceHeight < MIN_TENCENT_SIDE_PX) {
    const scale = MIN_TENCENT_SIDE_PX / Math.min(sourceWidth, sourceHeight)
    sourceWidth = Math.round(sourceWidth * scale)
    sourceHeight = Math.round(sourceHeight * scale)
  }
  if (sourceWidth > MAX_TENCENT_SIDE_PX || sourceHeight > MAX_TENCENT_SIDE_PX) {
    const scale = MAX_TENCENT_SIDE_PX / Math.max(sourceWidth, sourceHeight)
    sourceWidth = Math.round(sourceWidth * scale)
    sourceHeight = Math.round(sourceHeight * scale)
  }
  return { width: sourceWidth, height: sourceHeight }
}

export async function resizeImageToRequirements(file, { forcePng = false } = {}) {
  const bitmap = await createImageBitmap(file)
  const sourceWidth = bitmap.width
  const sourceHeight = bitmap.height
  bitmap.close()

  const { width: destinationWidth, height: destinationHeight } = clampDimensions(sourceWidth, sourceHeight)
  const canvas = document.createElement('canvas')
  canvas.width = destinationWidth
  canvas.height = destinationHeight
  const context = canvas.getContext('2d')
  const resizedImageBitmap = await createImageBitmap(file)
  context.drawImage(resizedImageBitmap, 0, 0, destinationWidth, destinationHeight)
  resizedImageBitmap.close()

  const mimeType = file.type || 'image/png'
  const supportsQuality = mimeType === 'image/jpeg' || mimeType === 'image/webp'
  let exportMime = supportsQuality ? mimeType : 'image/jpeg'
  if (forcePng) {
    exportMime = 'image/png'
  }

  function toBlobWithQuality(quality) {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, exportMime, quality)
    })
  }

  async function compressUnderMaxSize(currentQuality) {
    const result = await toBlobWithQuality(currentQuality)
    if (result.size <= maxFileSizeBytes || currentQuality <= 0.1) {
      return result
    }
    const nextQuality = Math.max(0.1, currentQuality - (0.05))
    return compressUnderMaxSize(nextQuality)
  }

  let blob
  if (forcePng) {
    // PNG is lossless and ignores the quality argument, so the compression loop cannot shrink it.
    blob = await toBlobWithQuality(1)
  } else {
    blob = await compressUnderMaxSize(1)
    if (blob.size > maxFileSizeBytes) {
      blob = await toBlobWithQuality(0.6)
    }
  }

  const outputMime = blob.type || mimeType
  let extension = 'png'
  if (outputMime === 'image/jpeg') {
    extension = 'jpg'
  } else if (outputMime === 'image/webp') {
    extension = 'webp'
  }
  const baseName = file.name.replace(/\.[^.]+$/, '') || file.name
  return new File([blob], `${baseName}.${extension}`, { type: outputMime })
}
